import { Stack } from "@fluentui/react";
import React from "react";
import { getExchangeMessageHTMLBody } from "../../../src/taskpane/services/ExchangeService";
import Loader from "../../../src/taskpane/components/Loader";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { displayHeight } from "../../../src/taskpane/config/constants";

const deviderItemStyles: React.CSSProperties = {
    background: "#f1f1f1",
    height: displayHeight,
    width: 5,
    cursor: "col-resize",
};

const htmlContentContainerStyles: React.CSSProperties = {
    height: "100%",
    width: "100%",
    position: "relative"
}

const htmlFrameStyles: React.CSSProperties = {
    height: "100%",
    width: "100%",
    border: "none",
    position: "relative",
    userSelect: "none",
    zIndex: 0
}

const htmlContentOverlayStyles: React.CSSProperties = {
    height: "100%",
    width: 75,
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1000000
}

type ExchangePreviewProps = {
    mailboxId: string;
    messageId: string;
    initialWidth: number;
    minWidth: number;
};

export const ExchangePreview = ({ mailboxId, messageId, initialWidth, minWidth }: ExchangePreviewProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isResizing, setIsResizing] = React.useState(false);
    const [htmlContent, setHTMLContent] = React.useState<string>();
    const [width, setWidth] = React.useState(initialWidth);

    const enableResize = React.useCallback(() => {
        setIsResizing(true);
    }, [setIsResizing]);

    const disableResize = React.useCallback(() => {
        setIsResizing(false);
    }, [setIsResizing]);

    const resize = React.useCallback(
        (e: MouseEvent) => {
            if (isResizing) {
                const offsetRight = document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
                if (offsetRight >= minWidth) {
                    setWidth(offsetRight);
                }
            }
        },
        [minWidth, isResizing, setWidth]
    );

    React.useEffect(() => {
        (async () => {
            setIsLoading(true);
            setHTMLContent(await getExchangeMessageHTMLBody(mailboxId, messageId));
            setIsLoading(false);
        })();
    }, [messageId, setIsLoading]);

    React.useEffect(() => {
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", disableResize);

        return () => {
            document.removeEventListener("mousemove", resize);
            document.removeEventListener("mouseup", disableResize);
        };
    }, [disableResize, resize]);

    return (
        <Stack horizontal styles={{ root: { height: displayHeight, width: width } }}>
            <Stack.Item>
                <div style={deviderItemStyles} onMouseDown={enableResize} />
            </Stack.Item>
            <Stack.Item grow>
                {isLoading
                    ? <Loader label={languageStrings.Loading} hideLabel={true} />
                    : (htmlContent && <div style={htmlContentContainerStyles}>
                        <iframe srcDoc={htmlContent} sandbox="" style={htmlFrameStyles} />
                        <div style={htmlContentOverlayStyles}></div>
                    </div>)}
            </Stack.Item>
        </Stack>
    );
};